(() => {
  // Native Shopify Bundles relationships; no product handles or variant IDs to maintain.
  const API_VERSION = '2026-07';
  const CACHE_MS = 60000;
  const MAX_CARDS = 3;
  const BUNDLE_FIELDS = `
    nodes { id availableForSale product { id handle onlineStoreUrl } }
    pageInfo { hasNextPage endCursor }
  `;

  class CartBundleRecommendations {
    constructor(containers) {
      this.containers = containers;
      this.country = containers[0].dataset.country;
      this.root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      this.relationships = new Map();
      this.products = new Map();
      this.components = new Map();
      this.requestId = 0;
      this.adding = false;
      containers.forEach((container) => container.addEventListener('click', (event) => {
        const button = event.target.closest('[data-add-bundle]');
        if (button && container.contains(button)) {
          event.preventDefault();
          this.addBundle(button);
        }
      }));
      window.addEventListener('cart:bundles:update', (event) => this.update(event.detail));
    }

    async query(query, variables) {
      const response = await fetch(`/api/${API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ query, variables: { ...variables, country: this.country } }),
        signal: AbortSignal.timeout(10000)
      });
      if (!response.ok) throw new Error('Bundle lookup unavailable');
      const result = await response.json();
      if (result.errors?.length || !result.data) throw new Error('Bundle lookup failed');
      return result.data;
    }

    async loadRelationships(ids) {
      const now = Date.now();
      const missing = ids.filter((id) => !this.relationships.has(id) || this.relationships.get(id).expires <= now);
      // Keep tokenless GraphQL query cost below Shopify's limit, even for large carts.
      for (let offset = 0; offset < missing.length; offset += 10) {
        const batch = missing.slice(offset, offset + 10);
        const data = await this.query(`
          query CartBundles($ids: [ID!]!, $country: CountryCode) @inContext(country: $country) {
            nodes(ids: $ids) { ... on ProductVariant {
              id groupedBy(first: 20) { ${BUNDLE_FIELDS} }
            } }
          }
        `, { ids: batch.map((id) => `gid://shopify/ProductVariant/${id}`) });
        for (let index = 0; index < batch.length; index += 1) {
          const node = data.nodes[index];
          let connection = node?.groupedBy;
          const bundles = [...(connection?.nodes || [])];
          while (connection?.pageInfo.hasNextPage) {
            const next = await this.query(`
              query MoreCartBundles($id: ID!, $after: String!, $country: CountryCode) @inContext(country: $country) {
                node(id: $id) { ... on ProductVariant {
                  groupedBy(first: 20, after: $after) { ${BUNDLE_FIELDS} }
                } }
              }
            `, { id: node.id, after: connection.pageInfo.endCursor });
            connection = next.node?.groupedBy;
            bundles.push(...(connection?.nodes || []));
          }
          this.relationships.set(batch[index], { bundles, expires: Date.now() + CACHE_MS });
        }
      }
      return ids.flatMap((id) => this.relationships.get(id)?.bundles || []);
    }

    async loadProduct(handle) {
      const cached = this.products.get(handle);
      if (cached && cached.expires > Date.now()) return cached.product;
      // Ajax Product API preserves the storefront's language and presentment currency.
      const response = await fetch(`${this.root}products/${encodeURIComponent(handle)}.js`, {
        headers: { 'Accept': 'application/json' },
        signal: AbortSignal.timeout(10000)
      });
      if (!response.ok) throw new Error('Bundle product unavailable');
      const product = await response.json();
      this.products.set(handle, { product, expires: Date.now() + CACHE_MS });
      return product;
    }

    async loadComponents(variantId) {
      const cached = this.components.get(variantId);
      if (cached && cached.expires > Date.now()) return cached.items;
      const items = [];
      let after = null;
      do {
        const data = await this.query(`
          query BundleComponents($id: ID!, $after: String, $country: CountryCode) @inContext(country: $country) {
            node(id: $id) { ... on ProductVariant {
              components(first: 50, after: $after) {
                nodes { quantity productVariant { id title sku product { title } } }
                pageInfo { hasNextPage endCursor }
              }
            } }
          }
        `, { id: `gid://shopify/ProductVariant/${variantId}`, after });
        const connection = data.node?.components;
        if (!connection) throw new Error('Bundle components unavailable');
        items.push(...connection.nodes);
        after = connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;
      } while (after);
      this.components.set(variantId, { items, expires: Date.now() + CACHE_MS });
      return items;
    }

    async update(cart) {
      if (this.adding) return;
      if (!cart || !Array.isArray(cart.items)) return;
      const requestId = ++this.requestId;
      this.display('');
      if (!cart.items.length) return;
      const ids = [...new Set(cart.items.map((item) => String(item.variant_id)))];
      const inCart = new Set(cart.items.map((item) => String(item.product_id)));
      try {
        const bundles = await this.loadRelationships(ids);
        if (requestId !== this.requestId) return;
        const shown = new Set();
        const cards = [];
        for (const bundle of bundles) {
          const productId = bundle.product.id.split('/').pop();
          if (!bundle.availableForSale || !bundle.product.onlineStoreUrl || inCart.has(productId) || shown.has(productId)) continue;
          try {
            const product = await this.loadProduct(bundle.product.handle);
            if (requestId !== this.requestId) return;
            const variantId = bundle.id.split('/').pop();
            const variant = product.variants.find((item) => String(item.id) === variantId);
            if (!product.available || !variant?.available) continue;
            const components = await this.loadComponents(variantId);
            if (requestId !== this.requestId) return;
            cards.push(this.renderCard(product, variant, cart.currency, components));
            shown.add(productId);
            if (cards.length === MAX_CARDS) break;
          } catch (error) {
            // A removed/unpublished product must not block other recommendations.
          }
        }
        if (requestId === this.requestId) this.display(cards.join(''));
      } catch (error) {
        // Recommendations are optional; cart and checkout remain usable on API failure.
        if (requestId === this.requestId) this.display('');
      }
    }

    async addBundle(button) {
      if (this.adding) return;
      const variantId = button.dataset.addBundle;
      if (!/^\d+$/.test(variantId)) return;
      this.adding = true;
      ++this.requestId;
      const status = button.closest('.cart-bundle-recommendation').querySelector('[data-bundle-status]');
      status.textContent = '';
      this.containers.forEach((container) => container.querySelectorAll('[data-add-bundle]').forEach((item) => {
        item.disabled = true;
      }));
      button.textContent = 'Adding…';
      button.setAttribute('aria-busy', 'true');
      let added = false;
      try {
        const response = await fetch(`${this.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          status.textContent = typeof error.description === 'string'
            ? error.description : 'This bundle could not be added. Please check its availability.';
          return;
        }
        added = true;
        // The cart page owns its quantities and totals; reload to keep all of them in sync.
        if (document.body.classList.contains('template-cart')) {
          window.location.reload();
          return;
        }
        const cartResponse = await fetch(`${this.root}cart.js`, {
          headers: { 'Accept': 'application/json' }, cache: 'no-store'
        });
        if (!cartResponse.ok) throw new Error('Cart refresh failed');
        const cart = await cartResponse.json();
        this.adding = false;
        window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
        this.update(cart);
      } catch (error) {
        // Do not retry a cart mutation automatically: it may already have succeeded.
        window.location.assign(`${this.root}cart`);
      } finally {
        this.adding = false;
        this.containers.forEach((container) => container.querySelectorAll('[data-add-bundle]').forEach((item) => {
          item.disabled = false;
        }));
        button.disabled = added;
        button.textContent = added ? 'Added' : 'Add bundle';
        button.removeAttribute('aria-busy');
      }
    }

    display(html) {
      this.containers.forEach((container) => {
        container.innerHTML = html;
        container.hidden = !html;
      });
    }

    escape(value) {
      return String(value ?? '').replace(/[&<>"']/g, (character) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      })[character]);
    }

    renderCard(product, variant, currency, components) {
      const money = (amount) => this.escape(new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: currency || window.Shopify?.currency?.active || 'USD'
      }).format(amount / 100));
      const url = this.escape(`${this.root}products/${encodeURIComponent(product.handle)}?variant=${variant.id}`);
      const image = variant.featured_image?.src || product.featured_image;
      const compare = variant.compare_at_price > variant.price ? `<s>${money(variant.compare_at_price)}</s>` : '';
      const contents = components.map(({ quantity, productVariant: item }) => {
        const variantName = item.title === 'Default Title' ? 'Item' : this.escape(item.title);
        return `<li><strong>${this.escape(quantity)} ×</strong> ${variantName}</li>`;
      }).join('');
      return `<article class="cart-bundle-recommendation">
        <p class="cart-bundle-recommendation__heading">Complete your kit</p>
        <div class="cart-bundle-recommendation__body">
          ${image ? `<a href="${url}" class="cart-bundle-recommendation__image" tabindex="-1" aria-hidden="true">
            <img src="${this.escape(image)}" alt="" width="80" height="80" loading="lazy">
          </a>` : ''}
          <div class="cart-bundle-recommendation__details">
            <a class="cart-bundle-recommendation__title" href="${url}">${this.escape(product.title)}</a>
            ${contents ? `<div class="cart-bundle-recommendation__contents">
              <p>Includes:</p>
              <ul>${contents}</ul>
            </div>` : ''}
            <p class="cart-bundle-recommendation__price"><strong>${money(variant.price)}</strong> ${compare}</p>
            <button type="button" class="cart-bundle-recommendation__button" data-add-bundle="${this.escape(variant.id)}">Add bundle</button>
            <p class="cart-bundle-recommendation__status" data-bundle-status role="status" aria-live="polite"></p>
          </div>
        </div>
      </article>`;
    }
  }

  const init = () => {
    const containers = [...document.querySelectorAll('[data-cart-bundle-recommendations]')];
    if (!containers.length) return;

    let recommendations = null;
    const start = (cart) => {
      if (recommendations) return;
      recommendations = new CartBundleRecommendations(containers);
      recommendations.update(cart);
    };

    if (document.body.classList.contains('template-cart')) {
      try {
        start(JSON.parse(containers[0].dataset.initialCart));
      } catch (error) {
        start(null);
      }
      return;
    }

    // The side-cart is closed on initial page load. Its recommendations only
    // need data once the drawer has received an authoritative cart update.
    window.addEventListener('cart:bundles:update', (event) => start(event.detail));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
